import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ShieldIcon from '@mui/icons-material/Shield';
import DevicesIcon from '@mui/icons-material/Devices';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { useSummaryQuery } from '../api/queries';
import CoverageBar from '../components/CoverageBar';
import SeverityChip from '../components/SeverityChip';
import { useErrorSnackbar } from '../components/ErrorSnackbar';
import DegradedBanner from '../components/DegradedBanner';
import type { IssueType, IssueSeverity } from '../data/types';

const ISSUE_TYPE_LABELS: Record<IssueType, string> = {
  missing_huntress: 'Missing Huntress',
  missing_threatlocker: 'Missing ThreatLocker',
  missing_msp360: 'Missing MSP360',
  orphaned_device: 'Orphaned Device',
  av_unhealthy: 'AV Unhealthy',
  av_not_protected: 'AV Not Protected',
  retired_with_active_agents: 'Retired w/ Active Agents',
};

const ISSUE_TYPE_SEVERITY: Record<IssueType, IssueSeverity> = {
  missing_huntress: 'critical',
  missing_threatlocker: 'warning',
  missing_msp360: 'warning',
  orphaned_device: 'warning',
  av_unhealthy: 'critical',
  av_not_protected: 'critical',
  retired_with_active_agents: 'critical',
};

function formatAuditDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Stat card with icon, label, and large value */
function StatCard({
  icon,
  label,
  value,
  subtitle,
  color,
  children,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
  color: string;
  children?: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <Card
      onClick={onClick}
      sx={(t) => ({
        height: '100%',
        borderColor: alpha(color, 0.15),
        ...(onClick
          ? {
              cursor: 'pointer',
              '&:hover': {
                borderColor: alpha(color, 0.3),
                boxShadow: `0 4px 20px ${alpha(color, 0.08)}`,
              },
            }
          : {}),
        ...t.applyStyles('light', {
          borderColor: alpha(color, 0.12),
          ...(onClick
            ? {
                '&:hover': {
                  borderColor: alpha(color, 0.25),
                },
              }
            : {}),
        }),
      })}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(color, 0.1),
              color,
            }}
          >
            {icon}
          </Box>
          <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
            {label}
          </Typography>
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            lineHeight: 1.1,
            fontFamily: 'var(--font-mono)',
            color,
            fontSize: { xs: '2rem', sm: '2.4rem' },
          }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mt: 0.5,
              fontSize: '0.75rem',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {subtitle}
          </Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

export default function ExecutiveSummaryPage() {
  const navigate = useNavigate();
  const [degradedDismissed, setDegradedDismissed] = useState(false);
  const { data, isLoading, isError } = useSummaryQuery({ includeRetired: true });
  const summary = data?.summary;
  const degradedPlatforms = data?.degradedPlatforms ?? [];
  const { showError } = useErrorSnackbar();
  const theme = useTheme();

  useEffect(() => {
    if (isError) {
      showError('Failed to load executive summary data');
    }
  }, [isError, showError]);

  if (isLoading && !data) {
    return (
      <Box sx={{ py: 4 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  const coveragePct = summary?.overallCoveragePct ?? 0;
  const coverageColor =
    coveragePct >= 95
      ? theme.palette.success.main
      : coveragePct >= 80
        ? theme.palette.warning.main
        : theme.palette.error.main;

  const issueEntries = (Object.entries(summary?.issuesByType ?? {}) as [IssueType, number][])
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);

  const totalIssues = issueEntries.reduce((sum, [, count]) => sum + count, 0);

  return (
    <Box>
      {!degradedDismissed && (
        <DegradedBanner
          degradedPlatforms={degradedPlatforms}
          onDismiss={() => setDegradedDismissed(true)}
        />
      )}

      <Typography variant="h5" sx={{ mb: 3 }}>
        Executive Summary
      </Typography>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            icon={<ShieldIcon fontSize="small" />}
            label="Overall Coverage"
            value={`${coveragePct}%`}
            color={coverageColor}
            onClick={() => navigate('/clients')}
          >
            <Box sx={{ mt: 1.5 }}>
              <CoverageBar value={coveragePct} showLabel={false} height={8} />
            </Box>
          </StatCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            icon={<WarningAmberIcon fontSize="small" />}
            label="Below Threshold"
            value={summary?.clientsBelowThreshold ?? 0}
            subtitle="clients < 95% coverage"
            color={
              (summary?.clientsBelowThreshold ?? 0) > 0
                ? theme.palette.warning.main
                : theme.palette.success.main
            }
            onClick={() => navigate('/clients?status=below-threshold')}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            icon={<DevicesIcon fontSize="small" />}
            label="Devices Audited"
            value={summary?.totalDevicesAudited ?? 0}
            subtitle="across all clients"
            color={theme.palette.info.main}
            onClick={() => navigate('/clients')}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            icon={<AccessTimeIcon fontSize="small" />}
            label="Last Audit"
            value={formatAuditDate(summary?.lastAuditAt ?? new Date().toISOString())}
            color={theme.palette.text.secondary}
          />
        </Grid>

        {(summary?.retiredDeviceCount ?? 0) > 0 && (
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard
              icon={<DeleteForeverIcon fontSize="small" />}
              label="Retired w/ Active Agents"
              value={summary?.retiredDeviceCount ?? 0}
              subtitle="retired devices with agents still running"
              color={theme.palette.error.main}
            />
          </Grid>
        )}
      </Grid>

      {/* Issues + Clients Needing Attention */}
      <Grid container spacing={2}>
        {/* Issues by Type */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontSize: '0.95rem' }}>
                  Issues by Type
                </Typography>
                {totalIssues > 0 && (
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: 'error.main',
                    }}
                  >
                    {totalIssues} total
                  </Typography>
                )}
              </Box>

              {issueEntries.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  No issues detected
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {issueEntries.map(([issueType, count]) => (
                    <Box
                      key={issueType}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1,
                        px: 1.5,
                        borderRadius: 1.5,
                        bgcolor: 'action.hover',
                      }}
                    >
                      <SeverityChip
                        severity={ISSUE_TYPE_SEVERITY[issueType]}
                        label={ISSUE_TYPE_LABELS[issueType]}
                      />
                      <Typography
                        sx={{
                          fontFamily: 'var(--font-mono)',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          color:
                            ISSUE_TYPE_SEVERITY[issueType] === 'critical'
                              ? 'error.main'
                              : 'warning.main',
                        }}
                      >
                        {count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Clients Needing Attention */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Typography variant="h6" sx={{ fontSize: '0.95rem', mb: 2 }}>
                Clients Needing Attention
              </Typography>

              {(summary?.clientsWithMostIssues ?? []).length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  All clients are healthy
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {(summary?.clientsWithMostIssues ?? []).map((item, index) => (
                    <Box
                      key={item.clientId}
                      onClick={() => navigate(`/clients/${item.clientId}/devices`)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1.25,
                        px: 1.5,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        bgcolor:
                          index === 0 ? alpha(theme.palette.error.main, 0.06) : 'transparent',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography
                          sx={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'text.secondary',
                            minWidth: 16,
                          }}
                        >
                          {index + 1}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: index === 0 ? 600 : 400,
                            fontSize: '0.875rem',
                          }}
                        >
                          {item.clientName}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'var(--font-mono)',
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            color: item.issueCount >= 5 ? 'error.main' : 'warning.main',
                          }}
                        >
                          {item.issueCount}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
                        >
                          issues
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
